import React, { useMemo } from 'react';
import { Box, Typography } from '@mui/material';
import { vortxTypography, PowerGraph } from '@vortx/shared-ui';

const HISTORY_WINDOW = 48; // 12 hours of history (left of NOW)
const HORIZON_WINDOW = 48; // 12 hours prediction (right of NOW)

/**
 * Transform twin WebSocket equipment data into PowerGraph's power flow format.
 *
 * PowerGraph expects: grid_to_load, grid_charges_bess, pv_to_load,
 * pv_charges_bess, bess_to_load, load, net_utility — all at timestamps.
 */
function twinToFlowData(history, horizon) {
    if (!history.length) return [];

    const data = [];
    const visible = history.slice(-HISTORY_WINDOW);

    for (const pt of visible) {
        const load = pt.load || 0;
        const pv = pt.solar || 0;
        const battCharge = pt.battery > 0 ? pt.battery : 0;
        const battDischarge = pt.battery < 0 ? Math.abs(pt.battery) : 0;
        const cold = pt.cold || 0;
        const ev = pt.ev || 0;
        const grid = Math.max(0, pt.grid || 0);

        // PV allocation: first serves load, excess charges battery
        const pvToLoad = Math.min(pv, load);
        const pvChargesBess = Math.min(pv - pvToLoad, battCharge);
        // Grid allocation: serves remaining load + charges battery
        const gridChargesBess = Math.max(0, battCharge - pvChargesBess);
        const gridToLoad = Math.max(0, grid - gridChargesBess);

        // Use actual sim_time from broadcast, fall back to tick-based calc
        const ts = pt.sim_time
            ? new Date(pt.sim_time).getTime()
            : new Date(2026, 0, 1).getTime() + pt.tick * 15 * 60 * 1000;

        // Use PowerGraph's expected RAW keys (it transforms internally)
        data.push({
            timestamp: ts,
            load: load + cold + ev,
            net_utility: grid,
            utility: gridToLoad,                    // → grid_to_load
            bess_charge_grid: -gridChargesBess,     // → grid_charges_bess (abs'd)
            solar_to_load: pvToLoad,                // → pv_to_load
            bess_charge_solar: -pvChargesBess,      // → pv_charges_bess (abs'd)
            bess_discharge: battDischarge,           // → bess_to_load
            cold_load: cold,                         // Cold storage sub-load line
            ev_load: ev,                             // EV charging sub-load line
            batt_soc: pt.batt_soc ?? null,           // Battery SOC % (second Y axis)
        });
    }

    // Append horizon prediction (faded via PowerGraph's existing forecast support)
    if (horizon?.agents && horizon.current_step != null) {
        const stepsToShow = Math.min(horizon.steps_ahead || HORIZON_WINDOW, HORIZON_WINDOW);
        const agentMap = { site_dna: 'battery', ev_fleet: 'ev', cold_storage: 'cold' };

        // Base horizon timestamps on nowTimestamp (which uses sim_time)
        const baseTs = data.length > 0 ? data[data.length - 1].timestamp : Date.now();

        for (let i = 1; i <= stepsToShow; i++) {
            const ts = baseTs + i * 15 * 60 * 1000;

            let battVal = 0;
            let coldVal = 0;
            let evVal = 0;
            for (const [agentId, plan] of Object.entries(horizon.agents)) {
                const val = plan[i - 1] || 0;
                const key = agentMap[agentId] || agentId;
                if (key === 'battery') battVal = val;
                else if (key === 'cold') coldVal = Math.max(0, val);
                else if (key === 'ev') evVal = Math.max(0, val);
            }

            const pv = (horizon.pv_kw && i - 1 < horizon.pv_kw.length)
                ? Math.max(0, horizon.pv_kw[i - 1]) : 0;
            const load = (horizon.load_kw && i - 1 < horizon.load_kw.length)
                ? horizon.load_kw[i - 1] : 0;

            const battCharge = battVal > 0 ? battVal : 0;
            const battDischarge = battVal < 0 ? Math.abs(battVal) : 0;
            const totalLoad = load + coldVal + evVal;

            const pvToLoad = Math.min(pv, totalLoad);
            const pvChargesBess = Math.min(pv - pvToLoad, battCharge);
            const gridChargesBess = Math.max(0, battCharge - pvChargesBess);
            const netGrid = Math.max(0, totalLoad - pv + battCharge - battDischarge);
            const gridToLoad = Math.max(0, netGrid - gridChargesBess);

            data.push({
                timestamp: ts,
                load: totalLoad,
                net_utility: netGrid,
                utility: gridToLoad,
                bess_charge_grid: -gridChargesBess,
                solar_to_load: pvToLoad,
                bess_charge_solar: -pvChargesBess,
                bess_discharge: battDischarge,
            });
        }
    }

    return data;
}

/**
 * LiveDispatchChart — Uses shared PowerGraph component with the same
 * stacking model as Site DNA's dispatch view.
 *
 * @param {{ history: Array, horizon: object }} props
 */
export default function LiveDispatchChart({ history, horizon }) {
    const flowData = useMemo(
        () => twinToFlowData(history, horizon),
        [history, horizon]
    );

    const nowTimestamp = useMemo(() => {
        if (!history.length) return null;
        const last = history[history.length - 1];
        if (last?.sim_time) return new Date(last.sim_time).getTime();
        return new Date(2026, 0, 1).getTime() + (last?.tick ?? 0) * 15 * 60 * 1000;
    }, [history]);

    // Live values from latest data point
    const liveValues = useMemo(() => {
        if (!history.length) return null;
        const pt = history[history.length - 1];
        return {
            load: Math.round(pt.load || 0),
            solar: Math.round(pt.solar || 0),
            battery: Math.round(pt.battery || 0),
            batt_soc: Math.round(pt.batt_soc ?? 0),
            cold: Math.round(pt.cold || 0),
            ev: Math.round(pt.ev || 0),
            grid: Math.round(pt.grid || 0),
        };
    }, [history]);

    if (!flowData.length) return null;

    const statSx = {
        fontFamily: vortxTypography.mono,
        fontSize: '0.6rem',
        fontWeight: 600,
        lineHeight: 1,
    };
    const labelSx = {
        fontFamily: vortxTypography.mono,
        fontSize: '0.45rem',
        color: '#888',
        textTransform: 'uppercase',
        letterSpacing: '0.06em',
        lineHeight: 1,
    };

    return (
        <Box sx={{ width: '100%', height: '100%', px: 1, pt: 0.5, display: 'flex', flexDirection: 'column' }}>
            {/* Live stats banner */}
            {liveValues && (
                <Box sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 2,
                    px: 1,
                    py: 0.5,
                    mb: 0.5,
                    borderBottom: '1px solid rgba(178, 140, 80, 0.15)',
                }}>
                    <Box>
                        <Typography sx={labelSx}>Load</Typography>
                        <Typography sx={{ ...statSx, color: '#f4f1ec' }}>{liveValues.load} <span style={{ fontSize: '0.45rem', fontWeight: 400 }}>kW</span></Typography>
                    </Box>
                    <Box>
                        <Typography sx={labelSx}>Grid</Typography>
                        <Typography sx={{ ...statSx, color: '#4488cc' }}>{liveValues.grid} <span style={{ fontSize: '0.45rem', fontWeight: 400 }}>kW</span></Typography>
                    </Box>
                    {liveValues.solar > 0 && (
                        <Box>
                            <Typography sx={labelSx}>Solar</Typography>
                            <Typography sx={{ ...statSx, color: '#6BBF59' }}>{liveValues.solar} <span style={{ fontSize: '0.45rem', fontWeight: 400 }}>kW</span></Typography>
                        </Box>
                    )}
                    <Box>
                        <Typography sx={labelSx}>BESS</Typography>
                        <Typography sx={{ ...statSx, color: liveValues.battery < 0 ? '#aa88ff' : liveValues.battery > 0 ? '#7766cc' : '#666' }}>
                            {liveValues.battery < 0 ? `↑${Math.abs(liveValues.battery)}` : liveValues.battery > 0 ? `↓${liveValues.battery}` : '0'}
                            <span style={{ fontSize: '0.45rem', fontWeight: 400 }}> kW</span>
                        </Typography>
                    </Box>
                    <Box>
                        <Typography sx={labelSx}>SOC</Typography>
                        <Typography sx={{ ...statSx, color: liveValues.batt_soc > 50 ? '#66bb6a' : liveValues.batt_soc > 20 ? '#f59e0b' : '#e74c3c' }}>
                            {liveValues.batt_soc}<span style={{ fontSize: '0.45rem', fontWeight: 400 }}>%</span>
                        </Typography>
                    </Box>
                    {liveValues.cold > 0 && (
                        <Box>
                            <Typography sx={labelSx}>Cold</Typography>
                            <Typography sx={{ ...statSx, color: '#7B68EE' }}>{liveValues.cold} <span style={{ fontSize: '0.45rem', fontWeight: 400 }}>kW</span></Typography>
                        </Box>
                    )}
                    {liveValues.ev > 0 && (
                        <Box>
                            <Typography sx={labelSx}>EV</Typography>
                            <Typography sx={{ ...statSx, color: '#00d4aa' }}>{liveValues.ev} <span style={{ fontSize: '0.45rem', fontWeight: 400 }}>kW</span></Typography>
                        </Box>
                    )}
                </Box>
            )}
            <Box sx={{ flex: 1, minHeight: 0 }}>
            <PowerGraph
                data={flowData}
                isDark
                isResilientMode
                showControls={false}
                legendPosition="bottom"
                currentTimestamp={nowTimestamp}
                availableKeys={[
                    'load', 'net_utility', 'utility', 'utility_residual',
                    'solar_to_load', 'bess_charge_solar',
                    'bess_discharge', 'bess_charge_grid',
                    'cold_load', 'ev_load', 'batt_soc',
                ]}
            />
            </Box>
        </Box>
    );
}
